import { useCallback, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { fetchApplicantsRequest } from "../../../redux/applicant/actions";
import {
  FETCH_APPLICANTS_REQUEST,
  SEARCH_APPLICANT_REQUEST
} from "../../../redux/applicant/actionTypes";
import { clearSpecificRequest } from "../../../redux/request/action";
import { Insights } from "../../templates/Insights";
import moment from "moment";

const InsightsPage = () => {
  const dispatch = useDispatch();

  const initialFailedReasonData = [
    {
      type: "Lacking Experience",
      description: "Lacking Experience",
      value: 0,
      percentage: 0
    },
    {
      type: "Competencies / Skills",
      description: "Missing competencies or skills",
      value: 0,
      percentage: 0
    },
    {
      type: "Incompatible Experience",
      description: "Incompatible Experience",
      value: 0,
      percentage: 0
    },
    {
      type: "Salary",
      description: "Salary expectation beyond budget",
      value: 0,
      percentage: 0
    },
    {
      type: "Residence",
      description: "Residence is too far",
      value: 0,
      percentage: 0
    },
    {
      type: "Others",
      value: 0,
      percentage: 0
    }
  ];

  const defaultData = {
    applicants: {
      passed: 0,
      failed: 0
    },
    shortlisted: {
      passed: 0,
      failed: 0
    },
    technical: {
      passed: 0,
      failed: 0
    },
    initialInterview: {
      passed: 0,
      failed: 0
    },
    psychological: {
      passed: 0,
      failed: 0
    },
    finalInterview: {
      passed: 0,
      failed: 0
    }
  };
  const [data, setData] = useState(defaultData);
  const [lostApplicantsData, setLostApplicantsData] = useState([
    {
      name: "Blacklisted",
      value: 0,
      barColor: "#262626",
      percentage: 0
    },
    {
      name: "No Show",
      value: 0,
      barColor: "#F26C1F",
      percentage: 0
    },
    {
      name: "No Response",
      value: 0,
      barColor: "#1876CC",
      percentage: 0
    }
  ]);

  const [applicantSources, setApplicantSources] = useState([
    {
      name: "Facebook",
      value: 0,
      barColor: "#1773EA",
      percentage: 0
    },
    {
      name: "Jobstreet",
      value: 0,
      barColor: "#0D367C",
      percentage: 0
    },
    {
      name: "Mynimo",
      value: 0,
      barColor: "#EA6321",
      percentage: 0
    },
    {
      name: "Linkedin",
      value: 0,
      barColor: "#0270AD",
      percentage: 0
    }
  ]);

  const [dateFilter, setDateFilter] = useState<Array<any>>([
    moment().startOf("month"),
    moment()
  ]);
  const applicants = useSelector(
    (state: RootStateOrAny) => state.applicant.applicants
  );
  const [otherReasons, setOtherReasons] = useState([])

  const getFailedApplicants = () => {
    return applicants.filter(
      (applicant: any) =>
        applicant.applicationStatus?.currentStatus === "Failed"
    );
  };

  const getOtherFailedReasons = () => {
    return getFailedApplicants().filter(
      (applicant: any) =>
        !initialFailedReasonData.some(
          (reasonData, index) => {
            if (index < 5) {
              return reasonData.description?.toLowerCase() ===
                applicant.applicationStatus.reason?.toLowerCase();
            }
            return false;
          }
        )
    );
  };


  const handleFailedReasonsData = useCallback(() => {
    const failedApplicants = getFailedApplicants();
    const failedData = initialFailedReasonData.map((reason: any) => ({
      ...reason,
    }));
    failedApplicants.forEach((applicant: any) => {
      failedData.forEach((reason: any) => {
        if (
          applicant.applicationStatus.reason?.toLowerCase() ===
          reason.description?.toLowerCase()
        ) {
          reason.value++;
        }
      });
    });
    initialFailedReasonData[5].value = getOtherFailedReasons().length;
    const total = failedData.reduce(
      (total: any, object: any) => object.value + total,
      0
    );
    return failedData.map(({ type, value }: any) => ({
      type,
      value,
      percentage: Math.round((value / total) * 100),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicants]);

  useEffect(() => {
    const otherFailedReasons = getOtherFailedReasons()?.map((item: any) => item.applicationStatus.reason);
    setOtherReasons(otherFailedReasons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicants]);

  const handleApplicantInsightsData = () => {
    return applicants.reduce(
      (accumulator: any, applicant: any) => {
        if (
          applicant.applicationStatus?.subStatus === "Accepted" &&
          applicant.applicationStatus?.currentStatus === "Job Offer"
        ) {
          accumulator["hired"]++;
        } else if (applicant.applicationStatus?.currentStatus === "Failed") {
          accumulator["failed"]++;
        }

        return accumulator;
      },
      { hired: 0, failed: 0 }
    );
  };

  const handleJobOfferData = () => {
    return applicants.reduce(
      (accumulator: any, applicant: any) => {
        if (applicant.applicationStatus?.currentStatus === "Job Offer") {
          switch (applicant.applicationStatus?.subStatus) {
            case "Accepted":
              accumulator["accepted"]++;
              break;
            case "Declined":
              accumulator["declined"]++;
              break;
            case "":
            case "Unassigned":
            case null:
              accumulator["waiting"]++;
              break;
          }
        }

        return accumulator;
      },
      { accepted: 0, declined: 0, waiting: 0 }
    );
  };

  const handleDateFilter = (e: any) => {
    if (e) {
      setDateFilter([...e]);
    }
  };

  const isLoading = useSelector(
    (state: RootStateOrAny) => state.applicant.isLoading
  );

  const incrementor = (
    currentStatus: string,
    failedPhase: string,
    phase: string,
    currentPropVal:
      | "applicants"
      | "finalInterview"
      | "initialInterview"
      | "psychological"
      | "shortlisted"
      | "technical"
  ) => {
    return {
      passed:
        currentStatus === phase
          ? (data[currentPropVal].passed += 1)
          : data[currentPropVal].passed,
      failed:
        currentStatus === "Failed" && failedPhase === phase
          ? (data[currentPropVal].failed += 1)
          : data[currentPropVal].failed
    };
  };

  const handleFunnelDate = () => {
    setData(defaultData);
    if (!isLoading) {
      return applicants.map((applicant: any) => {
        const value = applicant.applicationStatus;

        setData({
          applicants: {
            passed: value?.currentStatus === "New Applicant" ? data.applicants.passed += 1 : data.applicants.passed,
            failed:
              value?.currentStatus === "Failed" &&
                value?.failedPhase === "New Applicant"
                ? (data.applicants.failed += 1)
                : data.applicants.failed
          },
          shortlisted: incrementor(
            value?.currentStatus,
            value?.failedPhase,
            "Shortlisted",
            "shortlisted"
          ),
          technical: incrementor(
            value?.currentStatus,
            value?.failedPhase,
            "Technical Exam",
            "technical"
          ),
          initialInterview: incrementor(
            value?.currentStatus,
            value?.failedPhase,
            "Initial Interview",
            "initialInterview"
          ),
          psychological: incrementor(
            value?.currentStatus,
            value?.failedPhase,
            "Psychological Exam",
            "psychological"
          ),
          finalInterview: incrementor(
            value?.currentStatus,
            value?.failedPhase,
            "Final Interview",
            "finalInterview"
          )
        });
      });
    }
  };

  const handleApplicantSourcesData = () => {
    setApplicantSources([
      {
        name: "Facebook",
        value: 0,
        barColor: "#1773EA",
        percentage: 0
      },
      {
        name: "Jobstreet",
        value: 0,
        barColor: "#0D367C",
        percentage: 0
      },
      {
        name: "Mynimo",
        value: 0,
        barColor: "#EA6321",
        percentage: 0
      },
      {
        name: "Linkedin",
        value: 0,
        barColor: "#0270AD",
        percentage: 0
      }
    ]);
    if (!isLoading) {
      const applicantSourcesData = applicants.reduce(
        (accumulator: any, applicant: any) => {
          accumulator = applicantSources.map(source => {
            if (
              applicant.platformApplied
                ?.toLowerCase()
                .includes(source.name.toLowerCase())
            ) {
              source.value++;
            }

            return source;
          });

          return accumulator;
        },
        applicantSources
      );

      const total = applicantSourcesData.reduce(
        (total: any, object: any) => object.value + total,
        0
      );
      const sources = applicantSourcesData.map(
        ({ name, value, barColor }: any) => ({
          name,
          value,
          barColor,
          percentage: !isNaN(Math.round((value / total) * 100))
            ? Math.round((value / total) * 100)
            : 0
        })
      );
      setApplicantSources(sources);
    }
  };

  const handleLostApplicantsData = () => {
    setLostApplicantsData([
      {
        name: "Blacklisted",
        value: 0,
        barColor: "#262626",
        percentage: 0
      },
      {
        name: "No Show",
        value: 0,
        barColor: "#F26C1F",
        percentage: 0
      },
      {
        name: "No Response",
        value: 0,
        barColor: "#1876CC",
        percentage: 0
      }
    ]);
    if (!isLoading) {
      const lostApplicationData = applicants.reduce(
        (accumulator: any, applicant: any) => {
          accumulator = lostApplicantsData.map(source => {
            if (
              applicant.applicationStatus?.subStatus
                ?.toLowerCase()
                .includes(source.name.toLowerCase())
            ) {
              source.value++;
            }

            return source;
          });

          return accumulator;
        },
        lostApplicantsData
      );

      const total = lostApplicationData.reduce(
        (total: any, object: any) => object.value + total,
        0
      );

      const sources = lostApplicationData.map(
        ({ name, value, barColor }: any) => ({
          name,
          value,
          barColor,
          percentage: !isNaN(Math.round((value / total) * 100))
            ? Math.round((value / total) * 100)
            : 0
        })
      );

      setLostApplicantsData(sources);
    }
  };

  useEffect(() => {
    dispatch(
      fetchApplicantsRequest("", undefined, [
        ["createdAt", ">=", dateFilter[0].startOf('day').toDate()],
        ["createdAt", "<=", dateFilter[1].endOf('day').toDate()]
      ])
    );

    return () => {
      dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_REQUEST));
      dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dateFilter]);

  useEffect(
    () => {
      handleFunnelDate();
      handleApplicantSourcesData();
      handleLostApplicantsData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  return (
    <>
      <Insights
        isLoading={isLoading}
        onChange={handleDateFilter}
        funnelData={data}
        jobOfferData={handleJobOfferData()}
        applicantInsightsData={handleApplicantInsightsData()}
        failedReasonsData={handleFailedReasonsData()}
        otherFailedReasonsData={otherReasons}
        lostApplicantData={lostApplicantsData}
        applicationSources={applicantSources}
      />
    </>
  );
};

export default InsightsPage;
